import {
  DashboardDataTableColumnKind,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../../lib/messages';
import type {
  DashboardPublisherRow,
  DashboardNetworkRow,
  DashboardDemandIntegrationRow,
  DashboardDemandPartnerRow,
} from '../../model';
import { dashboardSrpm } from '../../format';
import { formatCurrency, formatNumber } from '../../../../lib/format';

export function dashboardNumberColumn<T>(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (row: T) => number | null | undefined,
  suffix = '',
) {
  return {
    id,
    header: dashboardT(key),
    kind: DashboardDataTableColumnKind.Text,
    value: (row: T) =>
      `${formatNumber(value(row), 1)}${value(row) === null || value(row) === undefined ? '' : suffix}`,
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
  } satisfies DashboardDataTableColumnDefinition<T>;
}

export function dashboardCurrencyColumn<T>(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (row: T) => number | null | undefined,
) {
  return {
    id,
    header: dashboardT(key),
    kind: DashboardDataTableColumnKind.Text,
    value: (row: T) => formatCurrency(value(row)),
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
  } satisfies DashboardDataTableColumnDefinition<T>;
}

export const dashboardTableSortValues = {
  publisher: (row: DashboardPublisherRow, key: string) => {
    const values: Record<string, string | number | null | undefined> = {
      name: row.publisherName,
      adRequests: row.adRequests,
      adImpressions: row.adImpressions,
      fillRate: row.adRequestsFilled,
      rpm: row.rpm,
      srpm: row.srpm,
      revenuePct: row.revenuePct,
      marginPct: row.networkMarginPct,
      revenue: row.revenue,
      publisherRevenue: row.pubRevenue,
      profit: row.networkProfit,
    };
    return values[key];
  },
  network: (row: DashboardNetworkRow, key: string) => {
    const values: Record<string, string | number | null | undefined> = {
      name: row.networkName,
      adRequests: row.adRequests,
      adImpressions: row.adImpressions,
      revenue: row.revenue,
      networkProfit: row.networkProfit,
    };
    return values[key];
  },
  integration: (row: DashboardDemandIntegrationRow, key: string) => {
    const values: Record<string, string | number | null | undefined> = {
      name: row.demandIntegrationName,
      bidRequests: row.bidRequests,
      bidResponses: row.bidResponses,
      bidRatio: row.bidRatio,
      winRatio: row.winRatio,
      fillRate: row.adRequestsFilled,
      revenue: row.revenue,
      srpm: row.srpm ?? dashboardSrpm(row.revenue, row.bidRequests),
    };
    return values[key];
  },
  partner: (row: DashboardDemandPartnerRow, key: string) => {
    const values: Record<string, string | number | null | undefined> = {
      name: row.demandPartnerName,
      dpAdRequests: row.dpAdRequests,
      adImpressions: row.adImpressions,
      bidFillRatio: row.bidFillRatio,
      bids: row.bids,
      winRatio: row.winRatio,
      marginPct: row.marginPct,
      wonBids: row.wonBids,

      bidRatio: row.bidRatio,
      revenue: row.revenue,
      srpm: row.srpm ?? dashboardSrpm(row.revenue, row.dpAdRequests),
      revenueCap:
        row.revenueCapEnabled && row.revenueCap && row.revenueCap > 0
          ? (row.revenue ?? 0) / row.revenueCap
          : undefined,
    };
    return values[key];
  },
};
